import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import type { SegmentHistoryResponse } from "./types";
import { segmentsQueryKeys } from "../../api/queryKeys";

import { instance } from "@/lib/axios/instance";

export function useSegmentHistory({
  segmentId,
  from,
  to,
  pageSize = 30,
  search,
  changeTypes,
}: {
  segmentId: string;
  from: Date;
  to?: Date;
  pageSize?: number;
  search?: string;
  changeTypes?: string[];
}) {
  return useInfiniteQuery<SegmentHistoryResponse, AxiosError>({
    queryKey: segmentsQueryKeys.history(
      segmentId,
      from,
      to,
      pageSize,
      search,
      changeTypes,
    ),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      instance
        .get<SegmentHistoryResponse>(
          `/api/v2/Segments/${segmentId}/history`,
          {
            params: {
              from: from.toISOString(),
              ...(to && { to: to.toISOString() }),
              page: pageParam,
              pageSize,
              ...(search && { search }),
              ...(changeTypes?.length && { changeTypes }),
            },
          },
        )
        .then((res) => res.data),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.data.length,
        0,
      );
      return totalFetched < lastPage.totalRecords
        ? allPages.length + 1
        : undefined;
    },
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    refetchOnMount: true, // Overrides the global false so a `segment:*` realtime invalidation (api/realtime.ts) refetches on return
    placeholderData: keepPreviousData,
  });
}
